import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { WorkOrderState } from '../types';
import { workOrderState } from './workOrderState';

export const workOrdersSlice = createSlice({
  name: 'workOrders',
  initialState: workOrderState,
  reducers: {
    setWorkOrder(
      state,
      action: PayloadAction<WorkOrderState['workOrderList']>,
    ) {
      state.workOrderList = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    setTotalRecordCounts: (state, action: PayloadAction<number>) => {
      state.totalRecordCount = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortField: (state, action: PayloadAction<string>) => {
      state.sortField = action.payload;
    },
    setFromRequestDate: (state, action: PayloadAction<string>) => {
      state.fromRequestDate = action.payload;
    },
    setToRequestDate: (state, action: PayloadAction<string>) => {
      state.toRequestDate = action.payload;
    },
    setRecordId: (state, action: PayloadAction<string>) => {
      state.recordId = action.payload;
    },
    setUserId: (state, action: PayloadAction<number | null>) => {
      state.userId = action.payload;
    },
    setStatus: (state, action: PayloadAction<'complete' | 'open'>) => {
      state.status = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.deleting = action.payload;
    },
    setToDelete: (state, action: PayloadAction<Array<number>>) => {
      state.toDelete = action.payload;
    },
    setCreating: (state, action: PayloadAction<boolean>) => {
      state.creating = action.payload;
    },
    setDocType: (state, action: PayloadAction<'pdf' | 'csv'>) => {
      state.docType = action.payload;
    },
    setIsExplortToggleOn: (state, action: PayloadAction<boolean>) => {
      state.isExportToggleOn = action.payload;
    },
    setFieldArray: (state, action: PayloadAction<string[]>) => {
      state.fieldArray = action.payload;
    },
    setSummaryReportDateRange: (
      state,
      action: PayloadAction<WorkOrderState['summaryReportDateRange']>,
    ) => {
      state.summaryReportDateRange = action.payload;
    },
    setSummaryReportToggle: (state, action: PayloadAction<boolean>) => {
      state.summaryReportToggle = action.payload;
    },
    setResetData: () => workOrderState,
  },
});

export const { actions: workOrderActions } = workOrdersSlice;

export const selectWorkOrders = (state: RootState) => state.workOrders;

export const selectWorkOrdersList = createSelector(
  selectWorkOrders,
  workOrders => workOrders.workOrderList,
);
export const selectWorkOrderTableColoumns = createSelector(
  selectWorkOrders,
  workOrders => workOrders.columns,
);
export const selectWorkOrderLimit = createSelector(
  selectWorkOrders,
  workOrders => workOrders.limit,
);
export const selectWorkOrderPageIndex = createSelector(
  selectWorkOrders,
  workOrders => workOrders.pageIndex,
);
export const selectWorkOrderTotalRecordCount = createSelector(
  selectWorkOrders,
  workOrders => workOrders.totalRecordCount,
);
export const selectWorkOrderSortBy = createSelector(
  selectWorkOrders,
  workOrders => workOrders.sortBy,
);
export const selectWorkOrderSortField = createSelector(
  selectWorkOrders,
  workOrders => workOrders.sortField,
);
export const selectWorkOrderFromRequestDate = createSelector(
  selectWorkOrders,
  workOrders => workOrders.fromRequestDate,
);
export const selectWorkOrderToRequestDate = createSelector(
  selectWorkOrders,
  workOrders => workOrders.toRequestDate,
);
export const selectWorkOrderRecordId = createSelector(
  selectWorkOrders,
  workOrders => workOrders.recordId,
);

export const selectWorkOrderUserId = createSelector(
  selectWorkOrders,
  workOrders => workOrders.userId,
);

export const selectWorkOrderStatus = createSelector(
  selectWorkOrders,
  workOrders => workOrders.status,
);

export const selectWorkOrderDeleting = createSelector(
  selectWorkOrders,
  workOrders => workOrders.deleting,
);

export const selectWorkOrderCreating = createSelector(
  selectWorkOrders,
  workOrders => workOrders.creating,
);

export const selectWorkOrderToDelete = createSelector(
  selectWorkOrders,
  workOrders => workOrders.toDelete,
);

export const selectDocType = createSelector(
  selectWorkOrders,
  workOrders => workOrders.docType,
);

export const selectIsExportToggleOn = createSelector(
  selectWorkOrders,
  workOrders => workOrders.isExportToggleOn,
);

export const selectFieldArray = createSelector(
  selectWorkOrders,
  workOrders => workOrders.fieldArray,
);

export const selectSummaryReportDateRange = createSelector(
  selectWorkOrders,
  workOrders => workOrders.summaryReportDateRange,
);

export const selectSummaryReportToggle = createSelector(
  selectWorkOrders,
  workOrders => workOrders.summaryReportToggle,
);

export default workOrdersSlice.reducer;
